import type { SpaceRequestDataWithFiles } from '@/api/data-contracts';
import useAdvertisingSpacesService from '@/features/spaces/api/advertisingSpaces.service';

import { spacesApi } from './spacesApi';

const { keys } = spacesApi;

export const useCreateSpaceMutation = () => {
  const { createAdvertisingSpace } = useAdvertisingSpacesService();
  return spacesApi.useFeatureMutation<
    Awaited<ReturnType<typeof createAdvertisingSpace>>,
    SpaceRequestDataWithFiles
  >({
    mutationFn: (form) => createAdvertisingSpace(form),
    invalidate: [keys.all],
  });
};

export const useUpdateSpaceMutation = () => {
  const { updateAdvertisingSpace } = useAdvertisingSpacesService();
  return spacesApi.useFeatureMutation<
    Awaited<ReturnType<typeof updateAdvertisingSpace>>,
    { spaceId: string; form: SpaceRequestDataWithFiles }
  >({
    mutationFn: ({ spaceId, form }) => updateAdvertisingSpace(spaceId, form),
    invalidate: [keys.all],
  });
};

export const useDeleteSpaceMutation = () => {
  const { deleteAdvertisingSpace } = useAdvertisingSpacesService();
  return spacesApi.useFeatureMutation<Awaited<ReturnType<typeof deleteAdvertisingSpace>>, string>({
    mutationFn: (spaceId) => deleteAdvertisingSpace(spaceId),
    invalidate: [keys.all],
  });
};

export const useUnpublishSpaceMutation = () => {
  const { unpublishSpace } = useAdvertisingSpacesService();
  return spacesApi.useFeatureMutation<Awaited<ReturnType<typeof unpublishSpace>>, string>({
    mutationFn: (spaceId) => unpublishSpace(spaceId),
    invalidate: [keys.all],
  });
};

export const useAddSpaceDraftMutation = () => {
  const { addSpaceDraft } = useAdvertisingSpacesService();
  return spacesApi.useFeatureMutation<
    Awaited<ReturnType<typeof addSpaceDraft>>,
    SpaceRequestDataWithFiles
  >({
    mutationFn: (form) => addSpaceDraft(form),
    invalidate: [keys.all],
  });
};

export const useUpdateSpaceDraftMutation = () => {
  const { updateDraft } = useAdvertisingSpacesService();
  return spacesApi.useFeatureMutation<
    Awaited<ReturnType<typeof updateDraft>>,
    { spaceId: string; form: SpaceRequestDataWithFiles }
  >({
    mutationFn: ({ spaceId, form }) => updateDraft(spaceId, form),
    invalidate: [keys.all],
  });
};

export const useDeleteSpaceDocumentMutation = () => {
  const { deleteSpaceDocument } = useAdvertisingSpacesService();
  return spacesApi.useFeatureMutation<
    Awaited<ReturnType<typeof deleteSpaceDocument>>,
    { spaceId: string; documentId: string }
  >({
    mutationFn: ({ spaceId, documentId }) => deleteSpaceDocument(spaceId, documentId),
    invalidate: false,
  });
};

export const useUpdateSpaceGeometryMutation = () => {
  const { updateSpaceGeometry } = useAdvertisingSpacesService();
  return spacesApi.useFeatureMutation<
    Awaited<ReturnType<typeof updateSpaceGeometry>>,
    { spaceId: string; geometry: string | null }
  >({
    mutationFn: ({ spaceId, geometry }) => updateSpaceGeometry(spaceId, geometry),
    invalidate: [keys.all],
  });
};
